import { inject, Injectable } from "@angular/core";
import { RisrFormTypeExpander, RisrExpandableField, RisrSchemaField } from "@fry-it/risr-core";
import { BlueprintStore } from "./blueprint-store";
import { Blueprint } from "./blueprint";

/**
 * This expander is responsible for expanding blueprint fields
 *
 * @category Forms
 * @example
 * ```typescript
 * const field = {
 *     id: 'id',
 *     label: 'Select assessments',
 *     expandType: 'blueprint_category',
 *     options: {
 *       "allowedBlueprints": ["blueprint1", "blueprint2"]
 *     },
 *     required: true,
 *     initialValue: []
 * };
 * const expanded = await this.formExpander.expand(field);
 * ```
 */
@Injectable()
export class BlueprintCategoryFieldExpander implements RisrFormTypeExpander {

    private readonly blueprintsStore = inject(BlueprintStore);
    provideTypes: string[] = ['blueprint_category'];
    async expand(field: RisrExpandableField): Promise<RisrSchemaField> {

        const allowedBlueprints = field.extra?.blueprints;
        let blueprints: Blueprint[] = [];
        if (allowedBlueprints) {
            blueprints = await this.blueprintsStore.getMany(allowedBlueprints);
        } else {
            blueprints = await this.blueprintsStore.getAll();
        }

        const categories = blueprints
            .flatMap(blueprint => blueprint.flatCategories())
            .map(category => ({
                name: category._id,
                label: category.name
            }));

        return {
            name: field.id,
            label: field.label,
            type: 'discrete_multiple',
            required: field.required,
            options: {
                categories: [
                    { name: '__all__', label: 'All' },
                    ...categories,
                ],
            },
            initialValue: field.initialValue
        }
    }
}